import {
  Search,
  Sparkles,
  Home,
  Newspaper,
  BookOpen,
  FileText,
  LayoutGrid,
  MessageSquare,
  GraduationCap,
  Calendar,
  Building2,
  Briefcase,
  FolderOpen,
  Users,
  Settings,
  Shield,
  BarChart3,
  Bell,
  Mail,
  Clock,
  ArrowRight,
  TrendingUp,
  Zap,
  Target,
  Award,
  Play,
  Headphones,
  Scale,
  Handshake,
  HelpCircle,
} from "lucide-react";
import type { FooterCategory, MegaMenuContent, NavCommandItem, NavItem, SocialLink } from "./types";

export const MEGA_MENUS: Record<string, MegaMenuContent> = {
  knowledge: {
    featuredContent: [
      { title: "HR Trends 2026", href: "/articles/hr-trends-2026", description: "The definitive guide to this year's HR landscape", tag: "Featured" },
      { title: "AI in HR", href: "/articles/ai-in-hr", description: "How AI is transforming talent management", tag: "Trending" },
      { title: "DEI Frameworks", href: "/articles/dei-frameworks", description: "Building inclusive workplace strategies", tag: "Popular" },
    ],
    popularTopics: [
      { label: "Compensation", href: "/knowledge-hub" },
      { label: "Employee Relations", href: "/knowledge-hub" },
      { label: "Talent Acquisition", href: "/knowledge-hub" },
      { label: "Performance Management", href: "/knowledge-hub" },
      { label: "HR Technology", href: "/knowledge-hub" },
      { label: "Learning & Development", href: "/knowledge-hub" },
    ],
    trendingTags: ["#RemoteWork", "#AIinHR", "#DEI", "#GreatResignation", "#EmployeeExperience", "#HRTech", "#Compliance2026"],
    collections: [
      { label: "HR Policy Templates", href: "/resources", description: "50+ ready-to-use templates" },
      { label: "Interview Guides", href: "/resources", description: "Structured interview frameworks" },
    ],
    guides: [
      { label: "HR Compliance Guide", href: "/guide" },
      { label: "Salary Benchmarking", href: "/guide" },
    ],
    quickLinks: [
      { label: "Popular Articles", href: "/articles" },
      { label: "Latest Research", href: "/research" },
      { label: "HR News", href: "/news" },
      { label: "White Papers", href: "/whitepapers" },
    ],
  },
  community: {
    featuredContent: [
      { title: "CHRO Club", href: "/chro-club", description: "Exclusive peer network for CHROs", tag: "Premium" },
      { title: "Discussions", href: "/discussions", description: "2,400+ active discussions", tag: "Popular" },
      { title: "Mentorship", href: "/mentorship", description: "Connect with HR mentors", tag: "New" },
    ],
    popularTopics: [
      { label: "Discussions", href: "/discussions" },
      { label: "Q&A", href: "/qa" },
      { label: "Mentorship", href: "/mentorship" },
      { label: "CHRO Club", href: "/chro-club" },
    ],
    trendingTags: ["#CHROClub", "#Mentorship", "#HRCommunity", "#Networking", "#Leadership", "#CareerGrowth"],
    newContent: [
      { label: "New: DEI Discussion Thread", href: "/discussions/dei-2026", date: "2h ago" },
      { label: "New: Compensation Q&A", href: "/qa/compensation-2026", date: "5h ago" },
    ],
    quickLinks: [
      { label: "Start a Discussion", href: "/discussions/create" },
      { label: "Ask a Question", href: "/qa/ask" },
      { label: "Find a Mentor", href: "/mentorship" },
    ],
  },
  learning: {
    featuredContent: [
      { title: "HR Certification Programs", href: "/certificates", description: "SHRM, HRCI, and more", tag: "Featured" },
      { title: "Masterclasses", href: "/learning", description: "Learn from industry leaders", tag: "Trending" },
    ],
    popularTopics: [
      { label: "Courses", href: "/learning" },
      { label: "Certifications", href: "/certificates" },
      { label: "Masterclasses", href: "/learning" },
      { label: "Webinars", href: "/events" },
    ],
    trendingTags: ["#SHRM-CP", "#HRCI", "#Leadership", "#HRTech", "#PeopleAnalytics", "#DEI"],
    collections: [
      { label: "HR Fundamentals", href: "/learning", description: "Start your HR journey" },
    ],
    guides: [
      { label: "How to Choose a Certification", href: "/guide" },
    ],
    quickLinks: [
      { label: "Browse All Courses", href: "/learning" },
      { label: "View Certificates", href: "/certificates" },
      { label: "Upcoming Webinars", href: "/events" },
    ],
  },
  resources: {
    featuredContent: [
      { title: "HR Templates", href: "/resources", description: "50+ ready-to-use templates", tag: "Popular" },
      { title: "HR Toolkits", href: "/resources", description: "Complete toolkits and playbooks", tag: "Featured" },
    ],
    popularTopics: [
      { label: "Templates", href: "/resources" },
      { label: "Toolkits", href: "/resources" },
      { label: "Guides", href: "/resources" },
      { label: "AI Search", href: "/ai-search" },
    ],
    trendingTags: ["#HRPolicies", "#Templates", "#Checklists", "#Hiring", "#Onboarding"],
    quickLinks: [
      { label: "All Templates", href: "/resources" },
      { label: "Policy Templates", href: "/resources" },
      { label: "HR Guides", href: "/resources" },
    ],
    collections: [
      { label: "Startup Toolkit", href: "/resources", description: "For new HR departments" },
    ],
  },
};

export const PUBLIC_NAV_ITEMS: NavItem[] = [
  { href: "/", label: "Home" },
  { href: "/news", label: "News" },
  { href: "/articles", label: "Articles" },
  { href: "/blogs", label: "Blogs" },
  { href: "/research", label: "Research" },
  { href: "/knowledge-hub", label: "Knowledge Hub", megaMenu: "knowledge" },
  { href: "/community", label: "Community", megaMenu: "community" },
  { href: "/learning", label: "Learning", megaMenu: "learning" },
  { href: "/events", label: "Events" },
  { href: "/organizations", label: "Organizations" },
  { href: "/career", label: "Jobs" },
  { href: "/resources", label: "Resources", megaMenu: "resources" },
];

export const PUBLIC_TOOL_ITEMS: NavItem[] = [
  { href: "/search", label: "Search" },
  { href: "/ai-search", label: "AI Search" },
];

export const AUTH_ITEMS: NavItem[] = [
  { href: "/auth/login", label: "Sign In" },
  { href: "/auth/register", label: "Join HR Shakti" },
];

export const COMMAND_ITEMS: NavCommandItem[] = [
  { label: "Home", description: "Back to the homepage", icon: Home, href: "/", category: "Navigation", keywords: ["homepage", "landing"] },
  { label: "News", description: "Browse HR news and announcements", icon: Newspaper, href: "/news", category: "Content", keywords: ["news", "updates", "announcements"] },
  { label: "Articles", description: "Read in-depth articles and blogs", icon: BookOpen, href: "/blogs", category: "Content", keywords: ["articles", "blogs", "stories"] },
  { label: "Research", description: "Access research reports and whitepapers", icon: FileText, href: "/research", category: "Content" },
  { label: "Knowledge Hub", description: "Explore HR topics and guides", icon: LayoutGrid, href: "/knowledge-hub", category: "Navigation" },
  { label: "Discussions", description: "Join community discussions", icon: MessageSquare, href: "/discussions", category: "Community", keywords: ["discussions", "forum", "threads"] },
  { label: "Q&A", description: "Ask and answer HR questions", icon: HelpCircle, href: "/qa", category: "Community" },
  { label: "Learning", description: "Browse courses and learning paths", icon: GraduationCap, href: "/learning", category: "Learning" },
  { label: "Certificates", description: "View your earned certificates", icon: Award, href: "/certificates", category: "Learning" },
  { label: "Masterclasses", description: "Learn from industry leaders", icon: Play, href: "/learning", category: "Learning" },
  { label: "Events", description: "Discover upcoming events and webinars", icon: Calendar, href: "/events", category: "Events" },
  { label: "Organizations", description: "Explore organization profiles", icon: Building2, href: "/organizations", category: "Community" },
  { label: "Jobs", description: "Find HR career opportunities", icon: Briefcase, href: "/career", category: "Career" },
  { label: "Search", description: "Search across all modules", icon: Search, href: "/search", category: "Tools" },
  { label: "AI Search", description: "AI-powered semantic search", icon: Sparkles, href: "/ai-search", category: "Tools" },
  { label: "Dashboard", description: "Your personal workspace", icon: Home, href: "/home", category: "Workspace", keywords: ["workspace", "profile"] },
  { label: "Settings", description: "Manage your account and preferences", icon: Settings, href: "/settings", category: "Workspace" },
  { label: "Notifications", description: "View your notifications", icon: Bell, href: "/notifications", category: "Workspace" },
  { label: "Mentorship", description: "Connect with HR mentors", icon: Users, href: "/mentorship", category: "Community" },
  { label: "HR Templates", description: "Browse HR templates and toolkits", icon: FolderOpen, href: "/resources", category: "Resources" },
];

export const FOOTER_CATEGORIES: FooterCategory[] = [
  {
    title: "Platform",
    links: [
      { label: "Knowledge Hub", href: "/knowledge-hub" },
      { label: "Community", href: "/community" },
      { label: "Learning", href: "/learning" },
      { label: "Events", href: "/events" },
      { label: "Organizations", href: "/organizations" },
      { label: "Jobs", href: "/career" },
    ],
  },
  {
    title: "Knowledge",
    links: [
      { label: "Articles", href: "/articles" },
      { label: "Blogs", href: "/blogs" },
      { label: "Research", href: "/research" },
      { label: "Whitepapers", href: "/whitepapers" },
      { label: "Reports", href: "/reports" },
      { label: "Resources", href: "/resources" },
    ],
  },
  {
    title: "Community",
    links: [
      { label: "Discussions", href: "/discussions" },
      { label: "Q&A", href: "/qa" },
      { label: "Mentorship", href: "/mentorship" },
      { label: "CHRO Club", href: "/chro-club" },
    ],
  },
  {
    title: "Learning",
    links: [
      { label: "Courses", href: "/learning" },
      { label: "Certifications", href: "/certificates" },
      { label: "Masterclasses", href: "/learning" },
      { label: "Webinars", href: "/events" },
    ],
  },
  {
    title: "Resources",
    links: [
      { label: "Templates", href: "/resources" },
      { label: "Toolkits", href: "/resources" },
      { label: "Guides", href: "/resources" },
      { label: "AI Search", href: "/ai-search" },
    ],
  },
  {
    title: "Support",
    links: [
      { label: "Contact Us", href: "/contact" },
      { label: "About", href: "/about" },
      { label: "Advertise", href: "/advertise" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About", href: "/about" },
      { label: "Contact", href: "/contact" },
      { label: "Careers", href: "/career" },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Privacy Policy", href: "/privacy" },
      { label: "Terms of Service", href: "/terms" },
      { label: "Cookie Policy", href: "/privacy" },
      { label: "Sitemap", href: "/sitemap" },
    ],
  },
];

export const SOCIAL_LINKS: SocialLink[] = [
  { href: "https://linkedin.com/company/hrshakti", label: "LinkedIn", icon: "Linkedin" },
  { href: "https://twitter.com/hrshakti", label: "Twitter", icon: "Twitter" },
  { href: "https://youtube.com/hrshakti", label: "YouTube", icon: "Youtube" },
  { href: "https://facebook.com/hrshakti", label: "Facebook", icon: "Facebook" },
  { href: "https://instagram.com/hrshakti", label: "Instagram", icon: "Instagram" },
];
